import {
  isAnonymousLanding,
  publicTagWindow,
  loadGoogleTag,
} from "./public-analytics";

// Booking details stay in Cal.com. Only an opaque booking ID reaches Google Ads.
export const CONSENT_KEY = "public-demo-measurement-v1";
const SENT_KEY = "public-demo-measured-bookings-v1";
const CONSENT_LIFETIME = 180 * 24 * 60 * 60 * 1000;
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

export function readConsent(win = window) {
  try {
    const saved = JSON.parse(win.localStorage.getItem(CONSENT_KEY));
    if (
      saved &&
      typeof saved.allowed === "boolean" &&
      saved.expires > Date.now()
    ) {
      return saved.allowed;
    }
  } catch (_) {
    /* Storage may be unavailable in private browsing. */
  }
  return null;
}

export function campaignAttribution(search, allowed) {
  if (!allowed) return {};
  const params = new URLSearchParams(search);
  return UTM_KEYS.reduce((result, key) => {
    const value = params.get(key);
    // Only campaign labels, never arbitrary URLs, email addresses or other fields.
    if (value && /^[a-zA-Z0-9_ .\-\u00C0-\u024F]{1,100}$/.test(value))
      result[key] = value;
    return result;
  }, {});
}

export function createDemoMeasurement({
  win = window,
  adsId = process.env.REACT_APP_GOOGLE_ADS_ID || "",
  conversionLabel = process.env.REACT_APP_GOOGLE_ADS_DEMO_LABEL || "",
} = {}) {
  let allowed = readConsent(win) === true;
  let initialized = false;
  let sent = new Set();
  const configured =
    /^AW-\d+$/.test(adsId) && /^[A-Za-z0-9_-]+$/.test(conversionLabel);
  try {
    sent = new Set(JSON.parse(win.sessionStorage.getItem(SENT_KEY)) || []);
  } catch (_) {
    /* Optional storage. */
  }

  function initTag() {
    if (!allowed || !configured || initialized || !isAnonymousLanding(win))
      return;
    const tagWindow = publicTagWindow(win);
    if (!tagWindow) return;
    publicTagWindow(win).gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    publicTagWindow(win).gtag("consent", "update", {
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "denied",
    });
    publicTagWindow(win).gtag("js", new Date());
    publicTagWindow(win).gtag("config", adsId, {
      send_page_view: false,
      allow_ad_personalization_signals: false,
      allow_google_signals: false,
      page_location: win.location.origin + win.location.pathname,
      page_referrer: "",
    });
    loadGoogleTag(tagWindow, adsId);
    initialized = true;
  }

  return {
    start: initTag,
    setConsent(value) {
      allowed = value === true;
      try {
        win.localStorage.setItem(
          CONSENT_KEY,
          JSON.stringify({ allowed, expires: Date.now() + CONSENT_LIFETIME }),
        );
      } catch (_) {
        /* Booking does not depend on storage. */
      }
      if (allowed) {
        initTag();
        if (initialized && isAnonymousLanding(win))
          publicTagWindow(win).gtag("consent", "update", {
            ad_storage: "granted",
            ad_user_data: "granted",
            ad_personalization: "denied",
          });
      } else if (initialized && isAnonymousLanding(win)) {
        publicTagWindow(win).gtag("consent", "update", {
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
        });
      }
    },
    recordRequest(uid) {
      if (
        !isAnonymousLanding(win) ||
        !allowed ||
        !configured ||
        typeof uid !== "string" ||
        !/^[a-zA-Z0-9_-]{6,100}$/.test(uid) ||
        sent.has(uid)
      )
        return false;
      initTag();
      publicTagWindow(win).gtag("event", "conversion", {
        send_to: adsId + "/" + conversionLabel,
        transaction_id: uid,
      });
      sent.add(uid);
      try {
        win.sessionStorage.setItem(
          SENT_KEY,
          JSON.stringify(Array.from(sent).slice(-100)),
        );
      } catch (_) {
        /* Google also deduplicates transaction_id. */
      }
      return true;
    },
  };
}
